// Web Push registration for the web app — the browser analogue of the mobile app's
// src/utils/push.ts. Registers the service worker, asks for notification permission, subscribes
// the browser to push with our VAPID public key, and registers that subscription with the API.
import { webPush } from '../api/client';

const SW_URL = '/service-worker.js';

// VAPID keys are base64url; PushManager.subscribe wants the public key as a Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Serialise a PushSubscription into the { endpoint, p256dh, auth } shape the API expects.
function toDto(sub) {
  const json = sub.toJSON();
  return {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
  };
}

// Idempotent: safe to call on every login / app load. Returns true if a subscription was
// registered, false if push is unavailable or permission was denied.
export async function registerWebPush() {
  if (!pushSupported()) {
    console.log('[push] This browser does not support Web Push.');
    return false;
  }

  try {
    const { data } = await webPush.vapidPublicKey();
    const publicKey = data?.publicKey;
    if (!publicKey) {
      console.warn('[push] Server returned no VAPID public key — web push not configured.');
      return false;
    }

    const registration = await navigator.serviceWorker.register(SW_URL);
    await navigator.serviceWorker.ready;

    if (Notification.permission === 'denied') {
      console.log('[push] Notification permission denied.');
      return false;
    }
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return false;
    }

    // Reuse an existing subscription, or create one. (Re-subscribing with the same key is fine.)
    let sub = await registration.pushManager.getSubscription();
    if (!sub) {
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    await webPush.register(toDto(sub));
    return true;
  } catch (err) {
    console.warn('[push] Web push registration failed:', err);
    return false;
  }
}

// Best-effort teardown on logout: unsubscribe the browser and drop the subscription server-side.
export async function unregisterWebPush() {
  if (!pushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = registration && (await registration.pushManager.getSubscription());
    if (!sub) return;
    try { await webPush.unregister({ endpoint: sub.endpoint }); } catch { /* ignore */ }
    await sub.unsubscribe();
  } catch {
    /* ignore — server prunes dead subscriptions on send too */
  }
}
